/**
 * Match Invoice Dialog ("laskutäsmäytys", #212)
 *
 * Matches a fully-received purchase order against its supplier invoice —
 * invoice number/date/total are recorded and the discrepancy against the
 * PO's own total is computed server-side and persisted.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DialogContent, TextField, Typography } from '@mui/material';
import { FormDialog } from '@/components/common/FormDialog';
import { useFormDirty } from '@/hooks/useFormDirty';
import { eurosToCents } from '@/utils/money';

interface MatchInvoiceDialogProps {
  open: boolean;
  onClose: () => void;
  orderNumber: string;
  poTotalCents: number;
  onConfirm: (data: { invoiceNumber: string; invoiceDate: string; invoiceTotalCents: number }) => void;
  isLoading?: boolean;
}

export function MatchInvoiceDialog({
  open,
  onClose,
  orderNumber,
  poTotalCents,
  onConfirm,
  isLoading,
}: MatchInvoiceDialogProps) {
  const { t } = useTranslation('inventory');
  const { isDirty, markDirty } = useFormDirty(open);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  // Raw typed text, not a formatted-and-reformatted derived value — avoids
  // the digit-scrambling bug already found in other price fields (#1068).
  const [invoiceTotalInput, setInvoiceTotalInput] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setInvoiceNumber('');
      setInvoiceDate('');
      setInvoiceTotalInput((poTotalCents / 100).toFixed(2));
      setError('');
    }
  }, [open, poTotalCents]);

  const handleSubmit = () => {
    if (!invoiceNumber.trim()) {
      setError(t('purchaseOrderDetail.invoiceMatch.numberRequired'));
      return;
    }
    if (!invoiceDate) {
      setError(t('purchaseOrderDetail.invoiceMatch.dateRequired'));
      return;
    }
    const invoiceTotalCents = eurosToCents(invoiceTotalInput);
    if (invoiceTotalCents == null || invoiceTotalCents < 0) {
      setError(t('purchaseOrderDetail.invoiceMatch.totalInvalid'));
      return;
    }

    onConfirm({
      invoiceNumber: invoiceNumber.trim(),
      invoiceDate: new Date(`${invoiceDate}T00:00:00.000Z`).toISOString(),
      invoiceTotalCents,
    });
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={t('purchaseOrderDetail.invoiceMatch.dialogTitle')}
      isDirty={isDirty}
      onSubmit={handleSubmit}
      submitLabel={t('purchaseOrderDetail.invoiceMatch.confirm')}
      isLoading={isLoading}
      maxWidth="xs"
      fullWidth
    >
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('purchaseOrderDetail.invoiceMatch.description', { orderNumber })}
        </Typography>
        <TextField
          autoFocus
          fullWidth
          label={t('purchaseOrderDetail.invoiceMatch.numberLabel')}
          value={invoiceNumber}
          onChange={(e) => { setInvoiceNumber(e.target.value); markDirty(); if (error) setError(''); }}
          sx={{ mb: 2 }}
          slotProps={{ htmlInput: { maxLength: 100 } }}
        />
        <TextField
          fullWidth
          type="date"
          label={t('purchaseOrderDetail.invoiceMatch.dateLabel')}
          value={invoiceDate}
          onChange={(e) => { setInvoiceDate(e.target.value); markDirty(); if (error) setError(''); }}
          sx={{ mb: 2 }}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          fullWidth
          label={t('purchaseOrderDetail.invoiceMatch.totalLabel')}
          helperText={
            error || t('purchaseOrderDetail.invoiceMatch.totalHint', { total: (poTotalCents / 100).toFixed(2) })
          }
          error={!!error}
          value={invoiceTotalInput}
          onChange={(e) => { setInvoiceTotalInput(e.target.value); markDirty(); if (error) setError(''); }}
          slotProps={{ input: { endAdornment: '€' } }}
        />
      </DialogContent>
    </FormDialog>
  );
}
